<template>
  <loan-status-alert-container :loan="loan">
    <template #borrower>
      <p v-if="exemptFromContributions">
        {{ $t("components.loan.status.alerts.completed.borrower.thanks") }}
      </p>
      <template v-if="!exemptFromContributions">
        <awareness-banner
          class="loan_contribution_thanks"
          :title="
            loan.platform_tip > 0
              ? $t('components.loan.status.alerts.completed.borrower.contribution.with_tip.title')
              : $t(
                  'components.loan.status.alerts.completed.borrower.contribution.without_tip.title'
                )
          "
          :image="'/coeur-du-partage-locomotion.svg'"
        >
          <template v-if="loan.platform_tip > 0">
            <p>
              {{
                $t(
                  "components.loan.status.alerts.completed.borrower.contribution.with_tip.message1"
                )
              }}
            </p>
            <p>
              {{
                $t(
                  "components.loan.status.alerts.completed.borrower.contribution.with_tip.message2"
                )
              }}
            </p>
            <p>
              {{
                $t(
                  "components.loan.status.alerts.completed.borrower.contribution.with_tip.message3"
                )
              }}
            </p>
            <div>
              <icon-button variant="primary" :href="$t('resources.contribute_url')">
                {{
                  $t(
                    "components.loan.status.alerts.completed.borrower.contribution.with_tip.learn_more"
                  )
                }}
              </icon-button>
            </div>
          </template>
          <template v-else>
            <p>
              {{
                $t(
                  "components.loan.status.alerts.completed.borrower.contribution.without_tip.message1"
                )
              }}
            </p>
            <p>
              {{
                $t(
                  "components.loan.status.alerts.completed.borrower.contribution.without_tip.message2"
                )
              }}
            </p>
            <p>
              {{
                $t(
                  "components.loan.status.alerts.completed.borrower.contribution.without_tip.message3"
                )
              }}
            </p>
            <div>
              <icon-button variant="primary" :href="$t('resources.contribute_url')">
                {{
                  $t(
                    "components.loan.status.alerts.completed.borrower.contribution.without_tip.contribute"
                  )
                }}
              </icon-button>
            </div>
          </template>
        </awareness-banner>
      </template>

      <template v-if="loan.borrower_invoice">
        <hr v-if="!exemptFromContributions" />
        <p>
          <icon-button
            icon="receipt"
            size="sm"
            variant="ghost-secondary"
            @click="$router.push(`/profile/invoices/${loan.borrower_invoice.id}`)"
            >{{ $t("components.loan.status.alerts.completed.borrower.invoice") }}</icon-button
          >
        </p>
      </template>
    </template>

    <template #owner>
      <p>{{ $t("components.loan.status.alerts.completed.owner.thanks") }}</p>
      <p v-if="loan.owner_invoice && isOwner">
        <icon-button
          icon="receipt"
          size="sm"
          variant="ghost-secondary"
          @click="$router.push(`/profile/invoices/${loan.owner_invoice.id}`)"
          >{{ $t("components.loan.status.alerts.completed.owner.invoice") }}</icon-button
        >
      </p>
      <template
        v-if="
          !loan.is_self_service &&
          loan.loanable.sharing_mode === 'hybrid' &&
          !loan.is_borrower_trusted
        "
      >
        <h4 class="alert-heading">
          {{ $t("components.loan.status.alerts.completed.owner.trusted_network.title") }}
        </h4>
        <p>
          {{
            $t("components.loan.status.alerts.completed.owner.trusted_network.message", {
              name: loan.borrower_user.name,
            })
          }}
        </p>
        <p>
          <icon-button
            variant="outline-success"
            icon="shield-fill-check"
            :onclick="addBorrowerToTrustedBorrowers"
          >
            {{
              $t("components.loan.status.alerts.completed.owner.trusted_network.add", {
                name: loan.borrower_user.name,
              })
            }}
          </icon-button>
          <b-button variant="link" :to="`/profile/loanables/${loan.loanable.id}#trusted_borrowers`">
            {{ $t("components.loan.status.alerts.completed.owner.trusted_network.view") }}
          </b-button>
        </p>
      </template>
    </template>

    <template #admin>
      <p v-if="isGlobalAdmin && (loan.borrower_invoice || loan.owner_invoice)">
        {{ $t("components.loan.status.alerts.completed.admin.invoices") }}
        <span class="button-list mt-2">
          <icon-button
            v-if="loan.borrower_invoice"
            icon="receipt"
            size="sm"
            variant="ghost-secondary"
            @click="$router.push(`/admin/invoices/${loan.borrower_invoice.id}`)"
            >{{ $t("components.loan.status.alerts.completed.admin.borrower_invoice") }}</icon-button
          >
          <icon-button
            v-if="loan.owner_invoice"
            icon="receipt"
            size="sm"
            variant="ghost-secondary"
            @click="$router.push(`/admin/invoices/${loan.owner_invoice.id}`)"
            >{{ $t("components.loan.status.alerts.completed.admin.owner_invoice") }}</icon-button
          >
        </span>
      </p>
      <p v-else>{{ $t("components.loan.status.alerts.completed.admin.completed") }}</p>
    </template>
  </loan-status-alert-container>
</template>

<script>
import LoanStatusAlertContainer from "@/components/Loan/Status/Alerts/LoanStatusAlertContainer.vue";
import AwarenessBanner from "@/components/shared/AwarenessBanner.vue";
import IconButton from "@/components/shared/IconButton.vue";
import { isOwner } from "@/helpers/permissions/loanables";
import { isGlobalAdmin } from "@/helpers/permissions/users";
import { addTrustedBorrower } from "@/requests/loanableUserRolesRequests";

export default {
  name: "LoanCompleted",
  components: {
    AwarenessBanner,
    LoanStatusAlertContainer,
    IconButton,
  },
  props: {
    loan: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isOwner() {
      return isOwner(this.$store.state.user, this.loan.loanable);
    },
    exemptFromContributions() {
      return this.loan.applicable_amount_types.contributions === "not_applicable";
    },
    isGlobalAdmin() {
      return isGlobalAdmin(this.$store.state.user);
    },
  },
  methods: {
    async addBorrowerToTrustedBorrowers() {
      await addTrustedBorrower(this.loan.loanable.id, this.loan.borrower_user.id);
      this.$emit(
        "input",
        { ...this.loan, is_borrower_trusted: true },
        {
          refreshDashboard: false,
          freshlyLoaded: false,
        }
      );
    },
  },
};
</script>

<style lang="scss">
@import "~bootstrap/scss/mixins/breakpoints";
.loan_contribution_thanks {
  grid-template-columns: 12rem auto;
  margin-top: -3rem;

  @include media-breakpoint-down(md) {
    grid-template-columns: 1fr;
    margin-top: -1rem;
  }

  .awareness-banner__image {
    // Unset object-fit: cover which affects svgs weirdly.
    object-fit: unset;
    max-width: 12rem;
  }
}
</style>
